import { useEffect, useRef } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';
import type { PageDto } from '@/lib/pagination';
import { useAuth } from '@/lib/auth/AuthContext';
import { uploadToS3 } from '@/features/uploads/api/uploadToS3';

export interface AudioSegmentOut {
  audio_url: string;
  created_at: string;
  duration: number | null;
  encounter_id: string;
  id: string;
  order: number;
  transcription: string | null;
}

export interface EncounterOut {
  assigned_to: string | null;
  clinical_context: Record<string, unknown> | null;
  clinician: UserSummary | null;
  created_at: string;
  created_by: string | null;
  current_revision_id: string | null;
  deleted_at: string | null;
  encounter_date: string;
  encounter_type: string | null;
  id: string;
  organization_id: string;
  patient: PatientSummary | null;
  /** null when the encounter is in the Inbox / Quick Start (Plan 07). */
  patient_id: string | null;
  pregnancy_id: string | null;
  status: string;
  transcript: string;
  updated_at: string;
}

export interface EncounterRevisionOut {
  clinical_context: Record<string, unknown> | null;
  created_at: string;
  created_by: string | null;
  encounter_id: string;
  id: string;
  revision_number: number;
  transcript: string;
}

export interface EncounterUpdateData {
  assigned_to?: string;
  clinical_context?: Record<string, unknown>;
  encounter_date?: string;
  encounter_type?: string;
  status?: string;
  transcript?: string;
}

export type PaginatedEncounters = PageDto<EncounterOut>;

export interface PatientSummary {
  id: string;
  mrn: string;
  name: string | null;
}

export interface UserSummary {
  id: string;
  name: string;
}

export function useAssignEncounter() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['encounters', orgId] });
      queryClient.invalidateQueries({ queryKey: ['encounters', orgId, variables.encounterId] });
      queryClient.invalidateQueries({ queryKey: ['documents', orgId] });
    },
    mutationFn: async ({ patient_id, encounterId }: { encounterId: string; patient_id: string }) => {
      const { data } = await clientApi.post<EncounterOut>(`${orgUrl(orgId!)}/${encounterId}/assign`, { patient_id });
      return data;
    },
  });
}

export function useBulkDeleteEncounters() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['encounters', orgId] });
    },
    mutationFn: async (encounterIds: string[]) => {
      const results = await Promise.allSettled(encounterIds.map((id) => clientApi.delete(`${orgUrl(orgId!)}/${id}`)));
      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length > 0) {
        throw new Error(`Failed to delete ${failed.length} of ${encounterIds.length} encounters`);
      }
    },
  });
}

export function useCreateEncounter() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['encounters', orgId] });
    },
    mutationFn: async (data: {
      encounter_data: Record<string, unknown>;
      status: 'draft' | 'submitted';
      files: File[];
    }) => {
      const formData = new FormData();
      formData.append('encounter_data', JSON.stringify(data.encounter_data));
      formData.append('status', data.status);
      data.files.forEach((file) => formData.append('files', file));

      const { data: result } = await clientApi.post<EncounterOut>(orgUrl(orgId!), formData, {
        timeout: 300000,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return result;
    },
  });
}

export function useDeleteEncounter() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['encounters', orgId] });
      queryClient.invalidateQueries({ queryKey: ['documents', orgId] });
      queryClient.invalidateQueries({ queryKey: ['signing-document', orgId] });
    },
    mutationFn: async (encounterId: string) => {
      await clientApi.delete(`${orgUrl(orgId!)}/${encounterId}`);
    },
  });
}

export function useEncounter(encounterId: string | undefined) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    enabled: !!orgId && !!encounterId,
    queryKey: ['encounters', orgId, encounterId],
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'submitted' ? 5000 : false;
    },
    queryFn: async () => {
      const { data } = await clientApi.get<EncounterOut>(`${orgUrl(orgId!)}/${encounterId}`);
      return data;
    },
  });
}

export function useEncounterAudioSegments(encounterId: string | undefined) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    enabled: !!orgId && !!encounterId,
    queryKey: ['encounters', orgId, encounterId, 'audio-segments'],
    queryFn: async () => {
      const { data } = await clientApi.get<AudioSegmentOut[]>(`${orgUrl(orgId!)}/${encounterId}/audio-segments`);
      return data;
    },
  });
}

/**
 * The documents generated from an encounter.
 *
 * `encounterStatus` is what tells this query that work is in flight. Without
 * it the poll keyed off the documents list, which is empty exactly while
 * generation is running, so `[].some(...)` was false and it never looked
 * again: submitting an encounter showed "No documents yet" over a report that
 * was being written at that moment, until the page was reloaded. The risk is
 * not the confusion but the obvious reaction to it, which is to press
 * "Generate Document" and produce a duplicate.
 *
 * It cannot poll merely because the list is empty. A generation that failed
 * leaves an encounter with no documents for good, and that would be a request
 * every five seconds for as long as the tab is open.
 */
export function useEncounterDocuments(encounterId: string | undefined, encounterStatus?: string) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const generating = encounterStatus === 'submitted';

  const query = useQuery({
    enabled: !!orgId && !!encounterId,
    queryKey: ['encounters', orgId, encounterId, 'documents'],
    queryFn: async () => {
      const { data } = await clientApi.get<import('./documents-api').GeneratedDocumentOut[]>(
        `${orgUrl(orgId!)}/${encounterId}/documents`,
      );
      return data;
    },
    refetchInterval: (q) => {
      // While the encounter is submitted there may be no row yet; once there
      // is one, it is filled in from `generating`, so both need the poll.
      if (generating) return 5000;
      return q.state.data?.some((d) => d.status === 'generating') ? 5000 : false;
    },
  });

  // One more fetch when generation ends, because the poll alone cannot close
  // the gap: `finalize_workflow_updates` writes the document and completes the
  // encounter together, so the last poll before that can land microseconds too
  // early and the interval is cancelled before another is due.
  const { refetch } = query;
  const wasGenerating = useRef(generating);
  useEffect(() => {
    if (wasGenerating.current && !generating) void refetch();
    wasGenerating.current = generating;
  }, [generating, refetch]);

  return query;
}

export function useEncounterRevisions(encounterId: string | undefined) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    enabled: !!orgId && !!encounterId,
    queryKey: ['encounters', orgId, encounterId, 'revisions'],
    queryFn: async () => {
      const { data } = await clientApi.get<EncounterRevisionOut[]>(`${orgUrl(orgId!)}/${encounterId}/revisions`);
      return data;
    },
  });
}

export function useEncounters(params?: {
  worklist?: boolean;
  limit?: number;
  offset?: number;
  patient_id?: string;
  date_from?: string;
  date_to?: string;
}, enabled = true) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    enabled: !!orgId && enabled,
    queryKey: ['encounters', orgId, params],
    queryFn: async () => {
      const { data } = await clientApi.get<PaginatedEncounters>(orgUrl(orgId!), { params });
      return data;
    },
  });
}

export function useUpdateEncounter(encounterId: string | undefined) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: EncounterUpdateData) => {
      const { data } = await clientApi.patch<EncounterOut>(`${orgUrl(orgId!)}/${encounterId}`, body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['encounters', orgId, encounterId] });
      queryClient.invalidateQueries({ queryKey: ['encounters', orgId, encounterId, 'revisions'] });
    },
  });
}

export function useUploadAudioSegment(encounterId: string | undefined) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['encounters', orgId, encounterId, 'audio-segments'] });
    },
    mutationFn: async ({
      file,
      order,
      fileName,
      duration,
    }: {
      file: Blob;
      order: number;
      fileName?: string;
      duration?: number;
    }) => {
      const namedFile = new File([file], fileName || `recording-${order}.webm`, {
        type: file.type || 'audio/webm',
      });
      const { fileUrl } = await uploadToS3(orgId!, namedFile, 'audio');
      const { data } = await clientApi.post<AudioSegmentOut>(
        `${orgUrl(orgId!)}/${encounterId}/audio-segments/commit`,
        { audio_url: fileUrl, order, duration: duration != null ? Math.round(duration) : undefined },
      );
      return data;
    },
  });
}

export function useDeleteAudioSegment(encounterId: string | undefined) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['encounters', orgId, encounterId, 'audio-segments'] });
    },
    mutationFn: async (segmentId: string) => {
      await clientApi.delete(`${orgUrl(orgId!)}/${encounterId}/audio-segments/${segmentId}`);
    },
  });
}

function orgUrl(orgId: string) {
  return `/api/v1/organizations/${orgId}/encounters`;
}

/** Load encounter history without silently limiting a long pregnancy to the first page. */
export function useEncounterTimeline(patientId: string) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  return useInfiniteQuery({
    queryKey: ['encounters', orgId, 'timeline', patientId],
    enabled: !!orgId && !!patientId,
    initialPageParam: 0,
    queryFn: async ({ pageParam }) =>
      (
        await clientApi.get<PaginatedEncounters>(orgUrl(orgId!), {
          params: { patient_id: patientId, limit: 100, offset: pageParam },
        })
      ).data,
    getNextPageParam: (last, pages) =>
      last.meta.has_next_page ? pages.reduce((n, page) => n + page.data.length, 0) : undefined,
  });
}
